import lodash from 'lodash'

import type {
  FontGroupFullSchemaFragment,
  ProjectSchemaDeepFragment,
} from 'gql/__generated__/graphql'
import { SuspendDataType } from 'gql/__generated__/graphql'

import type { WebPackageSettings } from '../../entities/ProjectBuild/types'
import { parsePako } from '../../utils/gzip'

export type WindowSettings = {
  id?: string // TODO
  runtimeSettings: WebPackageSettings['runtimeSettings']
  scormSettings: WebPackageSettings['scormSettings']
}

export type AnalyticsConfigOverride = {
  endpoint?: string
  auth?: string
  companyId?: string
  projectId?: string
  artifactId?: string
  exportDate?: string
  learnerId?: string
  learnerName?: string
  learnerEmail?: string
  lmsType?: string
  lmsVersion?: string
  sessionId?: string
  portalId?: string
  portalName?: string
}

export type AnalyticsConfig = {
  fetchUrl: string
  override: AnalyticsConfigOverride
}

export type WindowDefaultData = {
  data: {
    projects: {
      default: {
        project: ProjectSchemaDeepFragment
        runtimeSettings: WebPackageSettings['runtimeSettings']
      }
      [key: string]: {
        project: ProjectSchemaDeepFragment
        runtimeSettings: WebPackageSettings['runtimeSettings']
      }
    }
    projectId: string
  }
  settings: WindowSettings
  artifactId: string
  buildUrl: string
  compressedData?: string
  // Analytics config with override support (portalId/portalName live in override for portal courses)
  analyticsConfig?: AnalyticsConfig | null
  // Top-level fontsGroups packaged by web.builder.ts alongside compressedData
  // (NOT inside data.projects.default.project.fontsGroups). Inline portal V2
  // player needs them to inject @font-face — the SCORM iframe bundle injects
  // them itself, but inline rendering has no such bundle.
  fontsGroups?: FontGroupFullSchemaFragment[]
}

let defaultData: WindowDefaultData

export const getWindowData = (): WindowDefaultData | undefined => {
  if (defaultData) {
    return defaultData
  }
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const windowDefaultData = window.defaultData

  if (!windowDefaultData) {
    return undefined
  }
  const { settings, compressedData, analyticsConfig, artifactId, buildUrl } = windowDefaultData
  const data = parsePako<WindowDefaultData['data']>(compressedData)
  const settingsPrepared = lodash.cloneDeep(settings)
  if (lodash.get(settingsPrepared, 'runtimeSettings.suspendDataType', []).length === 0) {
    lodash.set(settingsPrepared, 'runtimeSettings.suspendDataType', [SuspendDataType.navigation])
  }
  defaultData = {
    data,
    settings: settingsPrepared,
    analyticsConfig,
    artifactId,
    buildUrl,
  }

  return defaultData
}
export const getWindowDataOrThrow = (): WindowDefaultData => {
  const data = getWindowData()
  if (!data) {
    throw new Error('No window default data')
  }
  return data
}

export const getWindowProject = (): ProjectSchemaDeepFragment => {
  return getWindowDataOrThrow().data.projects.default.project
}

export const getWindowSettings = (): WindowSettings => {
  const windowData = getWindowDataOrThrow()

  return {
    runtimeSettings: windowData.settings.runtimeSettings,
    scormSettings: windowData.settings.scormSettings,
  }
}

export const getBuildUrl = () => getWindowDataOrThrow().buildUrl
